class DetailshopDilutionCalculator extends HTMLElement {
  connectedCallback() {
    this.isGerman = this.dataset.language === "de";
    this.products = Array.isArray(window.DETAILSHOP_DILUTION_PRODUCTS) ? window.DETAILSHOP_DILUTION_PRODUCTS : [];
    this.imageCache = new Map();
    this.state = { brand: "", product: null, row: null, size: null, model: null, strength: null };
    this.brandList = this.querySelector("[data-brand-list]");
    this.productList = this.querySelector("[data-product-list]");
    this.applicationList = this.querySelector("[data-application-list]");
    this.productStep = this.querySelector("[data-product-step]");
    this.applicationStep = this.querySelector("[data-application-step]");
    this.customStep = this.querySelector("[data-custom-step]");
    this.workspace = this.querySelector("[data-workspace]");
    this.search = this.querySelector("[data-product-search]");
    this.sizeInput = this.querySelector("[data-size-input]");
    this.sizeUnit = this.querySelector("[data-size-unit]");
    this.strengthWrap = this.querySelector("[data-strength-wrap]");
    this.strengthInput = this.querySelector("[data-strength-input]");
    this.result = this.querySelector("[data-result]");
    this.customProductParts = this.querySelector("[data-custom-product-parts]");
    this.customWaterParts = this.querySelector("[data-custom-water-parts]");
    this.customApply = this.querySelector("[data-custom-ratio-apply]");
    this.renderBrands();
    this.bindEvents();
    this.applyUrlSelection();
  }

  bindEvents() {
    this.search?.addEventListener("input", () => this.renderProducts(this.search.value));
    this.querySelector("[data-size-presets]")?.addEventListener("click", (event) => {
      const button = event.target.closest("[data-size]");
      if (!button) return;
      this.querySelectorAll("[data-size]").forEach((item) => item.classList.toggle("is-active", item === button));
      this.sizeUnit.value = "ml";
      this.sizeInput.value = button.dataset.size;
      this.state.size = Number(button.dataset.size);
      this.result.hidden = false;
      this.updateResult();
    });
    const updateCustomSize = () => {
      this.querySelectorAll("[data-size]").forEach((item) => item.classList.remove("is-active"));
      const multiplier = this.sizeUnit.value === "l" ? 1000 : 1;
      const enteredSize = Number(this.sizeInput.value || 0);
      this.state.size = enteredSize > 0 ? enteredSize * multiplier : null;
      this.result.hidden = !this.state.size;
      this.updateResult();
    };
    this.sizeInput?.addEventListener("input", updateCustomSize);
    this.sizeUnit?.addEventListener("change", updateCustomSize);
    this.strengthInput?.addEventListener("input", () => {
      this.state.strength = Number(this.strengthInput.value);
      this.updateResult();
    });
    this.querySelector("[data-custom-dilution]")?.addEventListener("click", () => this.selectCustomDilution());
    const validateCustomRatio = () => {
      this.customApply.disabled = !(Number(this.customProductParts.value) > 0 && Number(this.customWaterParts.value) > 0);
    };
    this.customProductParts?.addEventListener("input", validateCustomRatio);
    this.customWaterParts?.addEventListener("input", validateCustomRatio);
    this.customApply?.addEventListener("click", () => this.applyCustomRatio());
  }

  renderBrands() {
    const renderedBrands = [...this.brandList.querySelectorAll("[data-brand]")];
    if (renderedBrands.length) {
      renderedBrands.forEach((button) => button.addEventListener("click", () => this.selectBrand(button.dataset.brand)));
      return;
    }
    const brands = [...new Set(this.products.map((product) => product.brand))].sort((a, b) => a.localeCompare(b, this.isGerman ? "de" : "nl"));
    this.brandList.replaceChildren(...brands.map((brand) => {
      const count = this.products.filter((product) => product.brand === brand).length;
      return this.choice(brand, `${count} ${this.isGerman ? (count === 1 ? "Produkt" : "Produkte") : (count === 1 ? "product" : "producten")}`, () => this.selectBrand(brand));
    }));
  }

  applyUrlSelection() {
    const parameters = new URLSearchParams(window.location.search);
    const requestedProduct = parameters.get("product");
    const requestedBrand = parameters.get("brand");
    const product = requestedProduct ? this.products.find((item) => item.handle === requestedProduct) : null;
    if (product) {
      this.selectBrand(product.brand, false);
      this.selectProduct(product, false);
      if (window.location.hash === "#verdunningscalculator") {
        window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
        window.setTimeout(() => this.applicationStep.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
      }
      return;
    }
    if (requestedBrand && this.products.some((item) => item.brand === requestedBrand)) this.selectBrand(requestedBrand, false);
  }

  selectBrand(brand, shouldScroll = true) {
    this.state.brand = brand;
    this.state.product = null;
    this.state.row = null;
    this.brandList.querySelectorAll("button").forEach((button) => button.classList.toggle("is-active", (button.dataset.brand || button.dataset.value) === brand));
    this.productStep.hidden = false;
    this.applicationStep.hidden = true;
    this.customStep.hidden = true;
    this.workspace.hidden = true;
    this.querySelector("[data-workspace-step-number]").textContent = "04";
    this.search.value = "";
    this.querySelector("[data-product-step-copy]").textContent = `${this.isGerman ? "Produkte von" : "Producten van"} ${brand} ${this.isGerman ? "mit verfügbaren Verdünnungsdaten." : "met beschikbare verdunningsregels."}`;
    this.renderProducts("");
    if (shouldScroll) this.productStep.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  selectCustomDilution() {
    this.state = { brand: "custom", product: null, row: null, size: null, model: null, strength: null };
    this.brandList.querySelectorAll("button").forEach((button) => button.classList.toggle("is-active", button.hasAttribute("data-custom-dilution")));
    this.productStep.hidden = true;
    this.applicationStep.hidden = true;
    this.workspace.hidden = true;
    this.result.hidden = true;
    this.customStep.hidden = false;
    this.customProductParts.value = "1";
    this.customWaterParts.value = "";
    this.customApply.disabled = true;
    this.customStep.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  applyCustomRatio() {
    const productParts = Number(this.customProductParts.value);
    const waterParts = Number(this.customWaterParts.value);
    if (!(productParts > 0 && waterParts > 0)) return;
    const ratio = `${this.formatNumber(productParts)}:${this.formatNumber(waterParts)}`;
    this.state.product = { title: this.isGerman ? "Eigene Verdünnung" : "Eigen verdunning", handle: "", note: "" };
    this.state.row = { application: this.isGerman ? "Eigenes Mischungsverhältnis" : "Eigen mengverhouding", ratio, note: "" };
    this.state.model = { type: "parts", productParts, waterParts, defaultValue: null };
    this.state.strength = null;
    this.state.size = null;
    this.sizeInput.value = "";
    this.sizeUnit.value = "ml";
    this.querySelectorAll("[data-size]").forEach((item) => item.classList.remove("is-active"));
    this.strengthWrap.hidden = true;
    this.result.hidden = true;
    this.querySelector("[data-workspace-step-number]").textContent = "03";
    this.workspace.hidden = false;
    this.workspace.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  renderProducts(query) {
    const needle = String(query || "").trim().toLocaleLowerCase(this.isGerman ? "de" : "nl");
    const products = this.products.filter((product) => product.brand === this.state.brand && (!needle || product.title.toLocaleLowerCase(this.isGerman ? "de" : "nl").includes(needle)));
    if (!products.length) {
      const empty = document.createElement("p");
      empty.className = "dilution-empty";
      empty.textContent = this.isGerman ? "Keine Produkte gefunden." : "Geen producten gevonden.";
      this.productList.replaceChildren(empty);
      return;
    }
    this.productList.replaceChildren(...products.map((product) => {
      const button = this.choice(product.title, `${product.rows.length} ${this.isGerman ? (product.rows.length === 1 ? "Anwendung" : "Anwendungen") : (product.rows.length === 1 ? "toepassing" : "toepassingen")}`, () => this.selectProduct(product));
      button.classList.add("dilution-choice--product");
      button.classList.toggle("is-active", this.state.product?.handle === product.handle);
      this.addProductImage(button, product);
      return button;
    }));
  }

  async addProductImage(button, product) {
    let imageUrl = this.imageCache.get(product.handle);
    if (imageUrl === undefined) {
      try {
        const response = await fetch(`/products/${encodeURIComponent(product.handle)}.js`);
        if (!response.ok) throw new Error("Productafbeelding niet beschikbaar");
        const productData = await response.json();
        imageUrl = productData.featured_image || "";
      } catch (error) {
        imageUrl = "";
      }
      this.imageCache.set(product.handle, imageUrl);
    }
    if (!imageUrl) return;
    const media = document.createElement("span");
    media.className = "dilution-choice__media";
    const image = document.createElement("img");
    image.src = imageUrl;
    image.alt = product.title;
    image.loading = "lazy";
    image.decoding = "async";
    media.append(image);
    button.prepend(media);
  }

  selectProduct(product, shouldScroll = true) {
    this.state.product = product;
    this.state.row = null;
    this.productList.querySelectorAll("button").forEach((button) => button.classList.toggle("is-active", button.dataset.value === product.title));
    this.querySelector("[data-selected-product]").textContent = `${product.brand} · ${product.title}`;
    const localizedRows = this.isGerman ? (product.rowsDe || product.rows) : product.rows;
    this.applicationList.replaceChildren(...localizedRows.map((row) => {
      const button = this.choice((row.application || (this.isGerman ? "Allgemeine Anwendung" : "Algemeen gebruik")), row.ratio, () => this.selectRow(row));
      button.querySelector("small").classList.add("dilution-choice__ratio");
      return button;
    }));
    this.applicationStep.hidden = false;
    this.workspace.hidden = true;
    if (shouldScroll) this.applicationStep.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  selectRow(row) {
    this.state.row = row;
    this.state.model = this.parseRatio(row.ratio);
    this.state.strength = this.state.model.defaultValue;
    this.state.size = null;
    this.sizeInput.value = "";
    this.sizeUnit.value = "ml";
    this.querySelectorAll("[data-size]").forEach((item) => item.classList.remove("is-active"));
    this.result.hidden = true;
    this.applicationList.querySelectorAll("button").forEach((button) => button.classList.toggle("is-active", button.dataset.value === ((row.application || (this.isGerman ? "Allgemeine Anwendung" : "Algemeen gebruik")))));
    this.setupStrength();
    this.workspace.hidden = false;
    this.updateResult();
    this.workspace.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  setupStrength() {
    const model = this.state.model;
    const adjustable = model && model.min != null && model.max != null && model.max > model.min;
    this.strengthWrap.hidden = !adjustable;
    if (!adjustable) return;
    this.strengthInput.min = model.min;
    this.strengthInput.max = model.max;
    this.strengthInput.step = model.type === "percent" || model.type === "dose" ? .1 : 1;
    this.strengthInput.value = model.defaultValue;
  }

  parseRatio(value) {
    const original = String(value || "").trim();
    const normalized = original.toLowerCase().replaceAll(",", ".").replace(/[–—]/g, "-");
    if (/^(gebruiksklaar|onverdund|puur|gebrauchsfertig|unverdünnt|pur)$/.test(normalized)) return { type: "percent", min: 100, max: 100, defaultValue: 100 };
    const split = normalized.match(/^(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)$/);
    if (split && Number(split[1]) + Number(split[2]) === 100) {
      const percentage = Number(split[1]);
      return { type: "percent", min: percentage, max: percentage, defaultValue: percentage };
    }
    const fixedMl = normalized.match(/(\d+(?:\.\d+)?)\s*ml\s*(?:op|auf)\s*(\d+(?:\.\d+)?)\s*ml\s*(?:water|wasser)/);
    if (fixedMl) {
      const product = Number(fixedMl[1]), water = Number(fixedMl[2]), percentage = product / (product + water) * 100;
      return { type: "percent", min: percentage, max: percentage, defaultValue: percentage };
    }
    const fixedLitres = normalized.match(/(\d+(?:\.\d+)?)\s*ml\s*(?:op|auf)\s*(\d+(?:\.\d+)?)\s*l(?:iter)?\s*(?:(?:warm|warmes)\s*)?(?:water|wasser)/);
    if (fixedLitres) {
      const product = Number(fixedLitres[1]), water = Number(fixedLitres[2]) * 1000, percentage = product / (product + water) * 100;
      return { type: "percent", min: percentage, max: percentage, defaultValue: percentage };
    }
    const perLitre = normalized.match(/(\d+(?:\.\d+)?)(?:\s*-\s*(\d+(?:\.\d+)?))?\s*ml\s*(?:\/\s*1\s*l|per\s+liter|pro\s+liter)/);
    if (perLitre) {
      const first = Number(perLitre[1]), second = Number(perLitre[2] || first);
      return { type: "dose", min: Math.min(first, second), max: Math.max(first, second), defaultValue: first };
    }
    const percentage = normalized.match(/(\d+(?:\.\d+)?)(?:\s*-\s*(\d+(?:\.\d+)?))?\s*%/);
    if (percentage) {
      const first = Number(percentage[1]), second = Number(percentage[2] || first);
      return { type: "percent", min: Math.min(first, second), max: Math.max(first, second), defaultValue: first };
    }
    const denominators = [...normalized.matchAll(/1\s*:\s*(\d+(?:\.\d+)?)/g)].map((match) => Number(match[1]));
    if (denominators.length) {
      if (/(puur|pur)/.test(normalized)) denominators.unshift(0);
      const min = Math.min(...denominators), max = Math.max(...denominators);
      return { type: "ratio", min, max, defaultValue: denominators[0] };
    }
    return { type: "guidance", label: original, defaultValue: null };
  }

  updateResult() {
    if (!this.state.product || !this.state.row || !this.state.model || !this.state.size) return;
    const { product, row, model, size } = this.state;
    const strength = this.state.strength ?? model.defaultValue;
    const productAmount = this.querySelector("[data-product-amount]");
    const waterAmount = this.querySelector("[data-water-amount]");
    const amounts = this.querySelector("[data-result-amounts]");
    const guidance = this.querySelector("[data-result-guidance]");
    this.querySelector("[data-result-title]").textContent = product.title;
    this.querySelector("[data-result-meta]").textContent = `${(row.application || (this.isGerman ? "Allgemeine Anwendung" : "Algemeen gebruik"))} · ${row.ratio} · ${this.formatAmount(size)} ${this.isGerman ? "gesamt" : "totaal"}`;
    const productLink = this.querySelector("[data-product-link]");
    productLink.hidden = !product.handle;
    if (product.handle) productLink.href = `/products/${product.handle}`;
    const note = row.note || product.note;
    const noteElement = this.querySelector("[data-result-note]");
    noteElement.hidden = !note;
    noteElement.textContent = note || "";

    if (model.type === "guidance") {
      amounts.hidden = true;
      guidance.hidden = false;
      guidance.textContent = `${row.ratio}. ${note || (this.isGerman ? "Folge den Anweisungen auf dem Produktetikett." : "Volg de aanwijzingen op het productetiket.")}`;
      return;
    }
    amounts.hidden = false;
    guidance.hidden = true;
    let concentrate = 0;
    if (model.type === "ratio") concentrate = strength === 0 ? size : size / (strength + 1);
    if (model.type === "percent") concentrate = size * strength / 100;
    if (model.type === "dose") concentrate = size - (size / (1 + strength / 1000));
    if (model.type === "parts") concentrate = size * model.productParts / (model.productParts + model.waterParts);
    const water = Math.max(0, size - concentrate);
    productAmount.textContent = this.formatAmount(concentrate);
    waterAmount.textContent = this.formatAmount(water);
    const strengthLabel = this.querySelector("[data-strength-label]");
    if (model.type === "ratio") strengthLabel.textContent = strength === 0 ? (this.isGerman ? "Unverdünnt" : "Puur") : `1:${this.formatNumber(strength)}`;
    if (model.type === "percent") strengthLabel.textContent = `${this.formatNumber(strength)}%`;
    if (model.type === "dose") strengthLabel.textContent = `${this.formatNumber(strength)} ml/L ${this.isGerman ? "Wasser" : "water"}`;
  }

  formatNumber(value) {
    return Number(value).toLocaleString(this.isGerman ? "de-DE" : "nl-NL", { maximumFractionDigits: 1 });
  }

  formatAmount(value) {
    const rounded = value < 10 ? Math.round(value * 10) / 10 : Math.round(value);
    if (rounded >= 1000) return `${(rounded / 1000).toLocaleString(this.isGerman ? "de-DE" : "nl-NL", { maximumFractionDigits: 2 })} L`;
    return `${rounded.toLocaleString(this.isGerman ? "de-DE" : "nl-NL", { maximumFractionDigits: 1 })} ml`;
  }

  choice(title, subtitle, handler) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "dilution-choice";
    button.dataset.value = title;
    const strong = document.createElement("strong");
    strong.textContent = title;
    const small = document.createElement("small");
    small.textContent = subtitle;
    button.append(strong, small);
    button.addEventListener("click", handler);
    return button;
  }
}

if (!customElements.get("dilution-calculator")) customElements.define("dilution-calculator", DetailshopDilutionCalculator);
